import { Graph } from '@antv/g6';

import { VisibilityState, EditorEvent, InlineEditState } from '../constants';
import { ItemData, ItemArg, Item } from '../types';
import { getGlobal } from './config';
import { normalizeItemData, getItem, doesItemExist } from './helpers';
import { getSelectedNodes, getSelectedEdges } from './graph';
import { getGraphLayers } from './layout';

/**
 * Get node or edge from graph
 */
export const get = (graph: Graph, item: ItemArg) => normalizeItemData(getItem(graph, item));

/**
 * Remove item with this id from the workspace
 */
export const remove = (graph: Graph, item: ItemArg) => {
  if (doesItemExist(graph, item)) {
    graph?.removeItem(item);
  }
};

/**
 * Update item with the given data
 */
export const update = (graph: Graph, { id, data }: ItemData) => graph?.updateItem(id, data);

/**
 * Updates the given item to be either selected or deselcted
 */
export const updateSelected = (graph: Graph, item: ItemArg, isSelected = false) => {
  const node = getItem(graph, item);
  if (node) {
    node.setState('selected', isSelected);
    // Fires the node select event so that we can listen for changes
    // For some reason we have to do this manually when selecting
    // programatically ¯\_(ツ)_/¯
    graph.emit('nodeselectchange', {
      selectedItems: {
        nodes: isSelected ? getSelectedNodes(graph) : [],
        edges: isSelected ? getSelectedEdges(graph) : [],
      },
      select: isSelected,
    });
  }
};

/**
 * Select Node by Id
 */
export const select = (graph: Graph, item: ItemArg) => updateSelected(graph, item, true);

/**
 * Unselect Node by Id
 */
export const unselect = (graph: Graph, item: ItemArg) => updateSelected(graph, item, false);

/**
 * Gets the order that the nodes should be in when doing a relayout
 * This prevents branches from crossing when the number of nodes on the right is
 * greater than the number on the left
 */
const getNodeOrderForLayout = (graph: Graph): string[] => {
  const layers = getGraphLayers(graph);

  /**
   * Layers are returned in an object that looks like the following:
   * {
   *  0: ['1', '2', '3'],
   *  1: ['4', '5'],
   *  2: ['6', '7'],
   * }
   *
   *  This function returns those layers in order.  EG:
   *  ['1', '2', '3', '4', '5', '6', '7']
   */

  return Object.keys(layers)
    .map((n) => parseInt(n, 10))
    .sort((a, b) => a - b)
    .reduce((acc: string[], curr) => [...acc, ...layers[curr]], []);
};

/**
 * Neatly relayout the workspace
 */
export const relayout = (graph: Graph) => {
  try {
    graph?.emit(EditorEvent.onBeforeRelayout, {});

    // TODO: [WF-3278] figure out how to accurately measure size
    graph?.updateLayout({
      type: 'dagre',
      rankdir: 'TB',
      nodesep: 100, // horiz
      ranksep: 75, // vert
      /**
       * Turning `workerEnabled` off as it was causing worker errors.
       * @see https://iterable.atlassian.net/browse/WF-5623
       *
       * NOTE: This is a temporary fix.  We should figure out why this is
       * happening and re-enable it, as having this off could cause performance
       * issues in large workflows.
       */
      workerEnabled: false,
      nodeOrder: getNodeOrderForLayout(graph),
      onLayoutEnd: () => graph?.emit(EditorEvent.onAfterRelayout, {}),
    });
  } catch (ex) {
    graph?.emit(EditorEvent.onAfterRelayout, {});
    throw ex; // continue to throw it
  }
};

/**
 * Set the state of an item.  EG:, selected, active, error etc
 */
export const updateItemState = (
  graph: Graph,
  item: ItemArg,
  stateName: string,
  stateValue: any,
) => {
  const node = getItem(graph, item);
  graph?.setItemState(node, stateName, stateValue);
};

/**
 * Programatically triggers inline edit
 */
export const showInlineEdit = (graph: Graph, item?: Item | string) => {
  if (!graph || !item) return;
  if (getGlobal(graph).plugin.inlineEditInput.state === VisibilityState.Show) return;

  graph.emit(EditorEvent.onInlineEditStateChange, {
    inlineEditState: InlineEditState.Show,
    item,
  });
};
